import { useEffect, useRef, useState } from 'react';

type ShadowTone = 'DAWN' | 'DAYLIGHT' | 'SUNSET' | 'MIDNIGHT';

type ShadowToneChangeDetail = {
  tone: ShadowTone;
};

const SHADOW_TONE_EVENT = 'taskshadowtonechange';
const DEFAULT_TONE: ShadowTone = 'DAYLIGHT';

const TONE_OPTIONS: Array<{ value: ShadowTone; label: string }> = [
  { value: 'DAWN', label: 'Dawn' },
  { value: 'DAYLIGHT', label: 'Daylight' },
  { value: 'SUNSET', label: 'Sunset' },
  { value: 'MIDNIGHT', label: 'Midnight' },
];

const SHADOW_WIDGET_MARKUP = `
  <style>
    :host {
      display: block;
    }

    .shadow-widget {
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 18px;
      background: linear-gradient(145deg, #111827 0%, #0f172a 100%);
      color: #f8fafc;
      padding: 20px;
      box-shadow: 0 20px 36px rgba(15, 23, 42, 0.28);
      font-family: var(--task-shadow-font, "Segoe UI", sans-serif);
    }

    .shadow-kicker {
      margin: 0 0 8px;
      color: #93c5fd;
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
    }

    h3 {
      margin: 0 0 8px;
      font-size: 1.25rem;
      color: #f8fafc;
    }

    .shadow-description {
      margin: 0 0 18px;
      color: #cbd5e1;
      line-height: 1.5;
    }

    .shadow-field {
      display: grid;
      gap: 8px;
      margin-bottom: 16px;
    }

    label {
      color: #e2e8f0;
      font-size: 0.92rem;
      font-weight: 600;
    }

    select {
      width: 100%;
      padding: 0.75rem 0.9rem;
      border: 1px solid #475569;
      border-radius: 12px;
      background: rgba(15, 23, 42, 0.9);
      color: #f8fafc;
      font-size: 0.95rem;
    }

    button {
      border: 0;
      border-radius: 12px;
      background: linear-gradient(135deg, #38bdf8 0%, #2563eb 100%);
      color: #eff6ff;
      cursor: pointer;
      font-size: 0.95rem;
      font-weight: 700;
      padding: 0.85rem 1rem;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      box-shadow: 0 14px 28px rgba(37, 99, 235, 0.28);
    }

    button:hover,
    button:focus-visible {
      transform: translateY(-1px);
      box-shadow: 0 18px 32px rgba(37, 99, 235, 0.34);
      outline: none;
    }

    .shadow-preview {
      display: grid;
      gap: 6px;
      margin-top: 18px;
      padding: 16px;
      border: 1px solid rgba(148, 163, 184, 0.28);
      border-radius: 14px;
      background: rgba(15, 23, 42, 0.72);
    }

    .shadow-preview-label {
      color: #93c5fd;
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .shadow-preview-value {
      color: #f8fafc;
      font-size: 1.1rem;
      font-weight: 700;
    }

    .shadow-hint-list {
      margin: 18px 0 0;
      padding-left: 1.1rem;
      color: #cbd5e1;
      display: grid;
      gap: 0.45rem;
      line-height: 1.45;
    }
  </style>
  <section class="shadow-widget" aria-labelledby="shadow-widget-title">
    <p class="shadow-kicker">Open shadow root</p>
    <h3 id="shadow-widget-title">Shadow Beacon Lab</h3>
    <p class="shadow-description">Choose a beacon tone inside the shadow root, then apply it to the host-level summary outside the widget.</p>
    <div class="shadow-field">
      <label for="shadow-tone-select">Beacon tone</label>
      <select id="shadow-tone-select" name="shadow-tone" data-shadow-tone-select>
        ${TONE_OPTIONS.map((option) => `<option value="${option.value}">${option.label}</option>`).join('')}
      </select>
    </div>
    <button type="button" data-shadow-apply-button>Apply to host summary</button>
    <div class="shadow-preview" aria-live="polite">
      <span class="shadow-preview-label">Current beacon tone</span>
      <strong class="shadow-preview-value" data-shadow-tone-output>Daylight</strong>
    </div>
    <ol class="shadow-hint-list">
      <li>Assert the host summary starts at <strong>Daylight</strong> before opening the shadow root.</li>
      <li>Inside the shadow root, change <strong>Beacon tone</strong> to a new value.</li>
      <li>Click <strong>Apply to host summary</strong> and verify the light-DOM summary matches.</li>
    </ol>
  </section>
`;

const formatToneLabel = (tone: ShadowTone) => {
  switch (tone) {
    case 'DAWN':
      return 'Dawn';
    case 'DAYLIGHT':
      return 'Daylight';
    case 'SUNSET':
      return 'Sunset';
    case 'MIDNIGHT':
      return 'Midnight';
    default:
      return tone;
  }
};

const isShadowTone = (value: string | null | undefined): value is ShadowTone => (
  TONE_OPTIONS.some((option) => option.value === value)
);

const getShadowElements = (host: HTMLDivElement | null) => {
  const shadowRoot = host?.shadowRoot;

  if (!shadowRoot) {
    return null;
  }

  const select = shadowRoot.querySelector<HTMLSelectElement>('[data-shadow-tone-select]');
  const applyButton = shadowRoot.querySelector<HTMLButtonElement>('[data-shadow-apply-button]');
  const output = shadowRoot.querySelector<HTMLElement>('[data-shadow-tone-output]');

  if (!select || !applyButton || !output) {
    return null;
  }

  return { select, applyButton, output };
};

const syncShadowWidget = (host: HTMLDivElement | null, tone: ShadowTone) => {
  const elements = getShadowElements(host);

  if (!host || !elements) {
    return;
  }

  elements.select.value = tone;
  elements.output.textContent = formatToneLabel(tone);
  host.dataset.shadowTone = tone;
};

export default function TaskShadowWidget() {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [mirroredTone, setMirroredTone] = useState<ShadowTone>(DEFAULT_TONE);

  useEffect(() => {
    const host = hostRef.current;

    if (!host) {
      return;
    }

    if (!host.shadowRoot) {
      host.attachShadow({ mode: 'open' });
    }

    if (!host.shadowRoot) {
      return;
    }

    if (!host.shadowRoot.hasChildNodes()) {
      host.shadowRoot.innerHTML = SHADOW_WIDGET_MARKUP;
    }

    const elements = getShadowElements(host);

    if (!elements) {
      return;
    }

    const handleSelectChange = () => {
      const nextTone = isShadowTone(elements.select.value)
        ? elements.select.value
        : DEFAULT_TONE;

      syncShadowWidget(host, nextTone);
    };

    const handleApplyClick = () => {
      const nextTone = isShadowTone(elements.select.value)
        ? elements.select.value
        : DEFAULT_TONE;

      syncShadowWidget(host, nextTone);
      elements.applyButton.dispatchEvent(
        new CustomEvent<ShadowToneChangeDetail>(SHADOW_TONE_EVENT, {
          detail: { tone: nextTone },
          bubbles: true,
          composed: true,
        })
      );
    };

    const handleToneChange = (event: Event) => {
      const nextTone = (event as CustomEvent<ShadowToneChangeDetail>).detail?.tone;

      if (isShadowTone(nextTone)) {
        setMirroredTone(nextTone);
      }
    };

    elements.select.addEventListener('change', handleSelectChange);
    elements.applyButton.addEventListener('click', handleApplyClick);
    host.addEventListener(SHADOW_TONE_EVENT, handleToneChange as EventListener);
    syncShadowWidget(host, DEFAULT_TONE);

    return () => {
      elements.select.removeEventListener('change', handleSelectChange);
      elements.applyButton.removeEventListener('click', handleApplyClick);
      host.removeEventListener(SHADOW_TONE_EVENT, handleToneChange as EventListener);
    };
  }, []);

  useEffect(() => {
    syncShadowWidget(hostRef.current, mirroredTone);
  }, [mirroredTone]);

  return (
    <div className="task-shadow-panel-body">
      <div className="task-shadow-panel-copy">
        <p className="task-shadow-panel-kicker">Training surface</p>
        <h3>Host traversal target</h3>
        <p>
          Start with the light-DOM host and mirrored summary, then move into the open shadow root for the
          labelled select and apply button. This training widget is isolated from task data and does not change
          any real task fields.
        </p>
        <p
          className="task-shadow-panel-summary"
          role="status"
          aria-live="polite"
          data-testid="task-shadow-summary"
        >
          Mirrored beacon tone: <strong>{formatToneLabel(mirroredTone)}</strong>
        </p>
      </div>

      <div className="task-shadow-widget-shell">
        <div
          ref={hostRef}
          className="task-shadow-widget-host"
          data-testid="task-shadow-widget-host"
          data-shadow-tone={mirroredTone}
          role="group"
          aria-label="Shadow beacon training widget"
        />
      </div>
    </div>
  );
}